import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  Item,
  SelectField,
  TextField,
  Text,
  OverlayProvider,
} from '@pingux/astro';
import {
  CredentialType,
  Environment,
  CreateCredentialRequest,
  OAuthCredentials,
  ApiKeyCredentials,
  AdminAccountCredentials,
  BasicAuthCredentials,
} from '@app-types/tap-partner-vault';
import { useAuthContext } from 'state/context/AuthContext';
import { isGroupOwnersGroup } from 'utils/auth';

interface CredentialFormProps {
  onSubmit: (data: CreateCredentialRequest) => Promise<void>;
  loading?: boolean;
}

const environments: Environment[] = ['production', 'staging', 'development', 'sandbox'];
const credentialTypes: { value: CredentialType; label: string }[] = [
  { value: 'oauth_client', label: 'OAuth Client' },
  { value: 'api_key', label: 'API Key' },
  { value: 'admin_account', label: 'Admin Account' },
  { value: 'basic_auth', label: 'HTTP Basic Auth' },
];

const CredentialForm: React.FC<CredentialFormProps> = ({
  onSubmit,
  loading = false,
}) => {
  const {
    state: { user },
  } = useAuthContext();
  const userRole = user?.role ?? 'user';
  const userGroups = user?.groups ?? [];
  const assignableGroups = userGroups.filter((group) => !isGroupOwnersGroup(group));
  const isAdminUser = userRole === 'admin' || userRole === 'superadmin';

  const [partner, setPartner] = useState<string>('');
  const [tenantId, setTenantId] = useState('');
  const [environment, setEnvironment] = useState<Environment>('production');
  const [credentialType, setCredentialType] = useState<CredentialType>('oauth_client');
  const [selectedGroupTags, setSelectedGroupTags] = useState<string[]>([]);
  const [groupTagsInput, setGroupTagsInput] = useState('');

  // OAuth credentials
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [tokenUrl, setTokenUrl] = useState('');

  // API Key credentials
  const [apiKey, setApiKey] = useState('');
  const [apiKeyUrl, setApiKeyUrl] = useState('');
  const [curlCommand, setCurlCommand] = useState('');

  // Admin Account credentials
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginUrl, setLoginUrl] = useState('');

  // Basic Auth credentials
  const [basicUsername, setBasicUsername] = useState('');
  const [basicPassword, setBasicPassword] = useState('');
  const [basicUrl, setBasicUrl] = useState('');
  const [basicCurlCommand, setBasicCurlCommand] = useState('');

  const [validationDisabled, setValidationDisabled] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const buildGroupTags = useCallback((): string[] => {
    if (!isAdminUser) return selectedGroupTags.filter((tag) => !isGroupOwnersGroup(tag));
    return Array.from(
      new Set(
        groupTagsInput
          .split(/[\n,]/)
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0 && !isGroupOwnersGroup(tag))
      )
    );
  }, [groupTagsInput, isAdminUser, selectedGroupTags]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    const groupTags = buildGroupTags();

    if (!partner) newErrors.partner = 'Partner is required';
    if (!tenantId.trim()) newErrors.tenantId = 'Tenant ID is required';
    if (groupTags.length === 0) {
      newErrors.groupTags = isAdminUser
        ? 'At least one group tag is required'
        : assignableGroups.length === 0
          ? 'Your access token does not include any assignable groups'
          : 'Select at least one group tag';
    }

    if (credentialType === 'oauth_client') {
      if (!clientId.trim()) newErrors.clientId = 'Client ID is required';
      if (!clientSecret.trim()) newErrors.clientSecret = 'Client Secret is required';
      if (!tokenUrl.trim()) newErrors.tokenUrl = 'Token URL is required';
    } else if (credentialType === 'api_key') {
      if (!apiKey.trim()) newErrors.apiKey = 'API Key is required';
      if (curlCommand.trim() && !curlCommand.includes('YOUR_API_KEY')) {
        newErrors.curlCommand = 'Curl command must include YOUR_API_KEY as the placeholder for the key';
      }
    } else if (credentialType === 'basic_auth') {
      if (!basicUsername.trim()) newErrors.basicUsername = 'Username is required';
      if (!basicPassword.trim()) newErrors.basicPassword = 'Password is required';
    } else if (credentialType === 'admin_account') {
      if (!username.trim()) newErrors.username = 'Username is required';
      if (!password.trim()) newErrors.password = 'Password is required';
      if (!loginUrl.trim()) newErrors.loginUrl = 'Login URL is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [partner, tenantId, buildGroupTags, isAdminUser, assignableGroups.length, credentialType, clientId, clientSecret, tokenUrl, apiKey, apiKeyUrl, curlCommand, basicUsername, basicPassword, basicUrl, username, password, loginUrl]);

  const buildCredentials = useCallback((): OAuthCredentials | ApiKeyCredentials | AdminAccountCredentials | BasicAuthCredentials => {
    if (credentialType === 'oauth_client') {
      return {
        client_id: clientId,
        client_secret: clientSecret,
        token_url: tokenUrl,
      };
    } else if (credentialType === 'api_key') {
      return {
        api_key: apiKey,
        url: apiKeyUrl || undefined,
        curl_command: curlCommand || undefined,
      };
    } else if (credentialType === 'basic_auth') {
      return {
        username: basicUsername,
        password: basicPassword,
        url: basicUrl || undefined,
        curl_command: basicCurlCommand || undefined,
      } as BasicAuthCredentials;
    } else {
      return {
        username,
        password,
        login_url: loginUrl,
      };
    }
  }, [credentialType, clientId, clientSecret, tokenUrl, apiKey, apiKeyUrl, curlCommand, basicUsername, basicPassword, basicUrl, basicCurlCommand, username, password, loginUrl]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    const data: CreateCredentialRequest = {
      partner,
      tenant_id: tenantId,
      environment,
      credential_type: credentialType,
      credentials: buildCredentials(),
      group_tags: buildGroupTags(),
      validation_disabled: validationDisabled,
      notes: notes.trim() || undefined,
      is_private: isPrivate || undefined,
    };

    await onSubmit(data);
  }, [validateForm, partner, tenantId, environment, credentialType, buildCredentials, buildGroupTags, validationDisabled, notes, isPrivate, onSubmit]);

  const formSectionStyle = {
    padding: '1.5rem',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    marginBottom: '1.5rem',
  };

  const sectionTitleStyle = {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '1rem',
    paddingBottom: '0.5rem',
    borderBottom: '1px solid #e5e7eb',
  };

  return (
    <OverlayProvider>
      <Box sx={{ maxWidth: '800px', width: '100%', margin: '0 auto' }}>
        {/* Basic Information */}
        <Box sx={formSectionStyle}>
          <Text sx={sectionTitleStyle}>Basic Information</Text>
          <Grid columns={[1, 1, 2]} gap="lg">
            <TextField
              label="Partner"
              value={partner}
              onChange={(e) => setPartner(e.target.value)}
              placeholder="Enter partner name"
              status={errors.partner ? 'error' : 'default'}
              helperText={errors.partner}
            />

            <TextField
              label="Tenant ID"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder="e.g., acme-corp"
              status={errors.tenantId ? 'error' : 'default'}
              helperText={errors.tenantId}
            />

            <SelectField
              label="Environment"
              selectedKey={environment}
              onSelectionChange={(key) => setEnvironment(key as Environment)}
            >
              {environments.map((env) => (
                <Item key={env}>{env.charAt(0).toUpperCase() + env.slice(1)}</Item>
              ))}
            </SelectField>

            <SelectField
              label="Credential Type"
              selectedKey={credentialType}
              onSelectionChange={(key) => setCredentialType(key as CredentialType)}
            >
              {credentialTypes.map((type) => (
                <Item key={type.value}>{type.label}</Item>
              ))}
            </SelectField>
          </Grid>
        </Box>

        {/* Group Tags */}
        <Box sx={formSectionStyle}>
          <Text sx={sectionTitleStyle}>Group Tags</Text>
          {isAdminUser ? (
            <Box>
              <Text sx={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                PingOne Groups
              </Text>
              <textarea
                value={groupTagsInput}
                onChange={(e) => setGroupTagsInput(e.target.value)}
                placeholder={'Admins\nPartner Engineering'}
                rows={4}
                style={{
                  width: '100%',
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  padding: '0.5rem 0.75rem',
                  border: errors.groupTags ? '1px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
              {errors.groupTags && (
                <Text sx={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
                  {errors.groupTags}
                </Text>
              )}
              <Text sx={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Enter one exact PingOne group name per line, or separate values with commas.
              </Text>
            </Box>
          ) : (
            <Box>
              <Text sx={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                Select one or more groups from your access token
              </Text>
              <select
                multiple
                value={selectedGroupTags}
                onChange={(e) => setSelectedGroupTags(Array.from(e.target.selectedOptions, (option) => option.value))}
                disabled={assignableGroups.length === 0}
                size={Math.min(Math.max(assignableGroups.length, 3), 8)}
                style={{
                  width: '100%',
                  fontSize: '0.875rem',
                  padding: '0.5rem',
                  border: errors.groupTags ? '1px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  backgroundColor: assignableGroups.length === 0 ? '#f9fafb' : 'white',
                  color: '#111827',
                }}
              >
                {assignableGroups.map((group) => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
              </select>
              {errors.groupTags && (
                <Text sx={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
                  {errors.groupTags}
                </Text>
              )}
              <Text sx={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                {assignableGroups.length === 0
                  ? 'You cannot create a credential until your access token includes PingOne groups.'
                  : 'Hold Cmd/Ctrl to select multiple groups.'}
              </Text>
            </Box>
          )}
          {buildGroupTags().length > 0 && (
            <Box sx={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
              {buildGroupTags().map((tag) => (
                <span
                  key={tag}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.2rem 0.55rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    backgroundColor: '#dbeafe',
                    color: '#1d4ed8',
                  }}
                >
                  {tag}
                </span>
              ))}
            </Box>
          )}
        </Box>

        {/* OAuth Client Credentials */}
        {credentialType === 'oauth_client' && (
          <Box sx={formSectionStyle}>
            <Text sx={sectionTitleStyle}>OAuth Client Credentials</Text>
            <Grid columns={[1, 1, 2]} gap="lg">
              <TextField
                label="Client ID"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="Enter client ID"
                status={errors.clientId ? 'error' : 'default'}
                helperText={errors.clientId}
              />

              <TextField
                label="Client Secret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="Enter client secret"
                type="password"
                status={errors.clientSecret ? 'error' : 'default'}
                helperText={errors.clientSecret}
              />

              <Box sx={{ gridColumn: '1 / -1' }}>
                <TextField
                  label="Token URL"
                  value={tokenUrl}
                  onChange={(e) => setTokenUrl(e.target.value)}
                  placeholder="https://auth.example.com/oauth/token"
                  status={errors.tokenUrl ? 'error' : 'default'}
                  helperText={errors.tokenUrl}
                />
              </Box>
            </Grid>
          </Box>
        )}

        {/* API Key Credentials */}
        {credentialType === 'api_key' && (
          <Box sx={formSectionStyle}>
            <Text sx={sectionTitleStyle}>API Key Credentials</Text>
            <Grid columns={1} gap="lg">
              <TextField
                label="API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter API key"
                type="password"
                status={errors.apiKey ? 'error' : 'default'}
                helperText={errors.apiKey}
              />

              <TextField
                label="URL (Optional)"
                value={apiKeyUrl}
                onChange={(e) => setApiKeyUrl(e.target.value)}
                placeholder="https://api.example.com"
              />

              <Box>
                <Text sx={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                  Validation Curl Command (Optional)
                </Text>
                <textarea
                  value={curlCommand}
                  onChange={(e) => setCurlCommand(e.target.value)}
                  placeholder={'curl https://api.example.com/v1/models \\\n  -H "Authorization: Bearer YOUR_API_KEY"'}
                  rows={3}
                  style={{
                    width: '100%',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    padding: '0.5rem 0.75rem',
                    border: errors.curlCommand ? '1px solid #dc2626' : '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
                {errors.curlCommand && (
                  <Text sx={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
                    {errors.curlCommand}
                  </Text>
                )}
                <Text sx={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Paste the curl command used to call the API, replacing your actual key with YOUR_API_KEY
                </Text>
              </Box>
            </Grid>
          </Box>
        )}

        {/* Admin Account Credentials */}
        {credentialType === 'admin_account' && (
          <Box sx={formSectionStyle}>
            <Text sx={sectionTitleStyle}>Admin Account Credentials</Text>
            <Grid columns={[1, 1, 2]} gap="lg">
              <TextField
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                status={errors.username ? 'error' : 'default'}
                helperText={errors.username}
              />

              <TextField
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                type="password"
                status={errors.password ? 'error' : 'default'}
                helperText={errors.password}
              />

              <Box sx={{ gridColumn: '1 / -1' }}>
                <TextField
                  label="Login URL"
                  value={loginUrl}
                  onChange={(e) => setLoginUrl(e.target.value)}
                  placeholder="https://admin.example.com/login"
                  status={errors.loginUrl ? 'error' : 'default'}
                  helperText={errors.loginUrl}
                />
              </Box>
            </Grid>
          </Box>
        )}

        {/* Basic Auth Credentials */}
        {credentialType === 'basic_auth' && (
          <Box sx={formSectionStyle}>
            <Text sx={sectionTitleStyle}>HTTP Basic Auth Credentials</Text>
            <Grid columns={[1, 1, 2]} gap="lg">
              <TextField
                label="Username"
                value={basicUsername}
                onChange={(e) => setBasicUsername(e.target.value)}
                placeholder="Enter username"
                status={errors.basicUsername ? 'error' : 'default'}
                helperText={errors.basicUsername}
              />
              <TextField
                label="Password"
                value={basicPassword}
                onChange={(e) => setBasicPassword(e.target.value)}
                placeholder="Enter password"
                type="password"
                status={errors.basicPassword ? 'error' : 'default'}
                helperText={errors.basicPassword}
              />
              <Box sx={{ gridColumn: '1 / -1' }}>
                <TextField
                  label="URL (Optional)"
                  value={basicUrl}
                  onChange={(e) => setBasicUrl(e.target.value)}
                  placeholder="https://example.com"
                />
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Text sx={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                  Validation Curl Command (Optional)
                </Text>
                <textarea
                  value={basicCurlCommand}
                  onChange={(e) => setBasicCurlCommand(e.target.value)}
                  placeholder={`curl -H "Authorization: Basic $(echo -n '<USERNAME>:<PASSWORD>' | base64)" https://example.com`}
                  rows={3}
                  style={{
                    width: '100%',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
                <Text sx={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Paste the curl command used to validate credentials. The server will substitute the actual base64-encoded credentials automatically.
                </Text>
              </Box>
            </Grid>
          </Box>
        )}

        {/* Notes */}
        <Box sx={formSectionStyle}>
          <Text sx={sectionTitleStyle}>Notes</Text>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any relevant notes about this credential..."
            rows={4}
            style={{
              width: '100%',
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              resize: 'vertical',
              boxSizing: 'border-box',
              color: '#111827',
            }}
          />
        </Box>

        {/* Private */}
        <Box sx={{ ...formSectionStyle, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <input
            id="is-private"
            type="checkbox"
            checked={isPrivate}
            onChange={(e) => {
              setIsPrivate(e.target.checked);
              if (e.target.checked) setValidationDisabled(true);
            }}
            style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
          />
          <label htmlFor="is-private" style={{ fontSize: '0.875rem', color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
            Private — only visible to me and SuperAdmins
          </label>
        </Box>

        {/* Validation */}
        <Box sx={{ ...formSectionStyle, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <input
            id="validation-disabled"
            type="checkbox"
            checked={validationDisabled || isPrivate}
            disabled={isPrivate}
            onChange={(e) => setValidationDisabled(e.target.checked)}
            style={{ width: '1rem', height: '1rem', cursor: isPrivate ? 'not-allowed' : 'pointer' }}
          />
          <label htmlFor="validation-disabled" style={{ fontSize: '0.875rem', color: isPrivate ? '#9ca3af' : '#374151', cursor: isPrivate ? 'not-allowed' : 'pointer', userSelect: 'none' }}>
            Cannot be validated — skip validation for this credential
          </label>
        </Box>

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
          <Button
            variant="primary"
            onPress={handleSubmit}
            isLoading={loading}
            isDisabled={!isAdminUser && assignableGroups.length === 0}
          >
            Save Credential
          </Button>
        </Box>
      </Box>
    </OverlayProvider>
  );
};

export default CredentialForm;
